import type { HydratedUser, TenantStatusName, TokenPayload } from "@packsize/identity-api-sdk";
import { useEffect } from "react";

import type { PendoConfig } from "../../types/pendo.d";

const createPendoConfig = (
	environment: string,
	decodedToken: TokenPayload,
	currentUser: HydratedUser,
	tenantStatus: TenantStatusName | undefined,
): PendoConfig => {
	const { id, email, sub, tid, Company } = decodedToken;

	return {
		visitor: {
			id,
			email,
			userName: sub,
			language: currentUser.preferredLanguage,
			browserLanguage: window.navigator.language,
			environment,
		},
		account: {
			id: tid,
			name: Company,
			environment,
			reportingLicense: currentUser.reportingLicenseTier,
			dimensioningLicense: currentUser.dimensioningLicenseTier,
			coreLicense: currentUser.coreLicenseTier,
			tenantStatus,
		},
	};
};

const initializePendo = (
	environment: string,
	decodedToken: TokenPayload | null | undefined,
	currentUser: HydratedUser | null | undefined,
	tenantStatus: TenantStatusName | undefined,
) => {
	if (typeof window === "undefined") {
		console.debug(
			"[@packsize/application-monitoring | Pendo] Skipping initialization — no window object (SSR environment).",
		);
		return;
	}

	if (window.pendo === undefined) {
		console.debug(
			"[@packsize/application-monitoring | Pendo] Skipping initialization — window.pendo is not available (script not loaded).",
		);
		return;
	}

	if (decodedToken === null || decodedToken === undefined) {
		console.debug(
			"[@packsize/application-monitoring | Pendo] Skipping initialization — decodedToken is not yet available.",
		);
		return;
	}

	if (currentUser === null || currentUser === undefined) {
		console.debug(
			"[@packsize/application-monitoring | Pendo] Skipping initialization — currentUser is not yet available.",
		);
		return;
	}

	const config = createPendoConfig(environment, decodedToken, currentUser, tenantStatus);

	console.debug(
		`[@packsize/application-monitoring | Pendo] Initializing for visitor "${config.visitor.id}" in environment "${environment}".`,
	);

	try {
		window.pendo.initialize(config);
		console.debug("[@packsize/application-monitoring | Pendo] Initialization complete.");
	} catch (e) {
		console.error("Unable to initialize Pendo. Unknown error.", e);
	}
};

export const usePendo = (
	environment: string,
	decodedToken: TokenPayload | null | undefined,
	currentUser: HydratedUser | null | undefined,
	tenantStatus: TenantStatusName | undefined,
) => {
	useEffect(() => {
		initializePendo(environment, decodedToken, currentUser, tenantStatus);
	}, [currentUser, tenantStatus, environment, decodedToken]);
};
